import { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from '../hooks/use-toast';
import { apiRequest } from '../lib/queryClient';
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Check, 
  X, 
  FileEdit, 
  Download, 
  Printer, 
  Mail, 
  ArrowLeft, 
  BanknoteIcon,
  Building,
  User,
  Briefcase,
  CalendarRange,
  FileSpreadsheet,
} from "lucide-react";

import { PremiumHeader } from '../components/ui/premium-header';
import { PremiumButton } from '../components/ui/premium-button';
import { StatusBadge, getEstimateStatusBadge } from '../components/ui/status-badge';
import { ItemTable } from '../components/ui/item-table';
import { InfoCard } from '../components/ui/info-card';
import { DateDisplay } from '../components/ui/date-display';
import { SignatureDisplay } from '../components/ui/signature-display';
import { StatCard } from '../components/ui/stat-card';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

// Import form component for editing
import EstimateForm from '../components/estimates/estimate-form';

// Import layout components
import Sidebar from '../components/layout/sidebar';
import TopNav from '../components/layout/top-nav';

import { formatPhone } from '../lib/validation';
import { getDisplayNotes } from '../lib/estimate-notes';
import { generatePdfHtml, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';

// Helper function to format currency
const formatCurrency = (amount: number | string) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(typeof amount === 'string' ? parseFloat(amount) : amount);
};

export default function PremiumEstimatePage() {
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id: string }>();
  const estimateId = id ? parseInt(id) : null;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConvertingToInvoice, setIsConvertingToInvoice] = useState(false);

  // Fetch estimate data only if we have an ID
  const { 
    data: estimate, 
    isLoading,
    error,
    refetch 
  } = useQuery({
    queryKey: [`/api/protected/estimates`, estimateId],
    queryFn: async () => {
      const response = await fetch(`/api/protected/estimates?id=${estimateId}`);
      if (!response.ok) throw new Error('Failed to fetch estimate');
      return response.json();
    },
    enabled: estimateId !== null && !isNaN(estimateId),
  });

  // Get days until expiry
  const daysUntilExpiry = estimate?.expiryDate 
    ? differenceInDays(new Date(estimate.expiryDate), new Date()) 
    : null;

  // Calculate if expired
  const isExpired = estimate?.expiryDate && daysUntilExpiry !== null && daysUntilExpiry < 0;

  // Update estimate status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const res = await apiRequest(
        "PATCH", 
        `/api/protected/estimates?id=${estimateId}`, 
        { status }
      );
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/protected/estimates`, estimateId] });
              toast({
          title: "Status Updated!",
          description: "The estimate status has been updated successfully.",
        });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al actualizar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Convert to invoice mutation
  const convertToInvoiceMutation = useMutation({
    mutationFn: async () => {
      setIsConvertingToInvoice(true);
      const res = await apiRequest(
        "POST", 
        `/api/protected/estimates?id=${estimateId}&action=convert-to-invoice`, 
        {}
      );
      return await res.json();
    },
    onSuccess: (data) => {
      setIsConvertingToInvoice(false);
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
              toast({
          title: "Work Order Created!",
          description: "The estimate has been successfully converted to a work order. The estimate status has been updated to 'converted'.",
        });
      // Stay on estimate page, status will be updated
    },
    onError: (error: Error) => {
      setIsConvertingToInvoice(false);
      toast({
        title: "Error al crear orden de trabajo",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle status change
  const handleStatusChange = (status: string) => {
    updateStatusMutation.mutate(status);
  };

  // Handle convert to invoice
  const handleConvertToInvoice = () => {
    if (estimate?.status !== 'accepted') {
      toast({
        title: "Action not allowed",
        description: "Only accepted estimates can be converted to work orders.",
        variant: "destructive",
      });
      return;
    }
    
    convertToInvoiceMutation.mutate();
  };

  // If no estimate ID, show creation form
  if (!estimateId) {
    return (
      <div className="remodra-layout">
        <Sidebar />
        <div className="remodra-main">
          <TopNav />
          <div className="remodra-content">
            <main className="p-8">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-4">Create Premium Estimate</h1>
                  <p className="text-slate-300">This page is for viewing existing premium estimates. To create a new estimate, please use the standard estimate creation form.</p>
                </div>
                <div className="text-center">
                  <Button onClick={() => setLocation('/estimates')}>
                    Back to Estimates
                  </Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="remodra-layout">
        <Sidebar />
        <div className="remodra-main">
          <TopNav />
          <div className="remodra-content">
            <main className="p-8">
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="loading-spinner"></div>
                <p className="mt-4 text-muted-foreground">Cargando estimado...</p>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="remodra-layout">
        <Sidebar />
        <div className="remodra-main">
          <TopNav />
          <div className="remodra-content">
            <main className="p-8">
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="text-center">
                  <h2 className="text-2xl font-bold">Could not load the estimate</h2>
                  <p className="mt-2 text-muted-foreground">
                    {error instanceof Error ? error.message : "Ha ocurrido un error"}
                  </p>
                  <Button 
                    variant="default" 
                    onClick={() => refetch()} 
                    className="mt-4"
                  >
                    Reintentar
                  </Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="remodra-layout">
      <Sidebar />
      <div className="remodra-main">
        <TopNav />
        <div className="remodra-content">
          <main className="p-8">
            <div className="container mx-auto">

      {/* Back button and actions */}
      <div className="flex justify-between items-center mb-6">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setLocation('/estimates')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Estimates
        </Button>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              if (!estimate) return;
              
              const printWindow = window.open('', '_blank');
              if (!printWindow) {
                toast({
                  title: "Popup Blocked",
                  description: "Please allow popups to download PDF.",
                  variant: "destructive"
                });
                return;
              }

              try {
                const config = DEFAULT_PDF_TEMPLATE_CONFIG;
                const htmlContent = generatePdfHtml({
                  data: estimate,
                  config,
                  type: 'estimate',
                  isSigned: estimate.status === 'accepted' && !!estimate.clientSignature
                });

                // Write HTML to new window and trigger print (allows Save as PDF)
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                
                // Wait for content to load, then trigger print dialog (user can Save as PDF)
                setTimeout(() => {
                  printWindow.print();
                }, 250);
                
                toast({
                  title: "PDF Download",
                  description: "Print dialog opened. Select 'Save as PDF' to download.",
                });
              } catch (error) {
                console.error("Error generating PDF:", error);
                toast({
                  title: "PDF Generation Failed",
                  description: "There was an error generating the PDF. Please try again.",
                  variant: "destructive"
                });
              }
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          {estimate.status !== 'rejected' && estimate.status !== 'expired' && (
            <Button variant="outline" size="sm">
              <Mail className="mr-2 h-4 w-4" />
              Send by Email
            </Button>
          )}
        </div>
      </div>

      {/* Premium header */}
      <PremiumHeader 
        title={`Estimate #${estimate.estimateNumber}`}
        subtitle="Review the estimate details and confirm your approval or make necessary changes."
        className="mb-6"
      />
      
      {/* Status and main actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Estimate Status</span>
              {getEstimateStatusBadge(estimate.status)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {estimate.status === 'draft' && (
                <>
                  <PremiumButton
                    variant="default"
                    onClick={() => handleStatusChange('sent')}
                    icon={<Mail className="h-4 w-4" />}
                    loading={updateStatusMutation.isPending}
                  >
                    Mark as Sent
                  </PremiumButton>
                  <PremiumButton
                    variant="outline"
                    onClick={() => setIsEditModalOpen(true)}
                    icon={<FileEdit className="h-4 w-4" />}
                  >
                    Edit Estimate
                  </PremiumButton>
                </>
              )}
              
              {estimate.status === 'sent' && (
                <>
                  <PremiumButton
                    variant="success"
                    onClick={() => handleStatusChange('accepted')}
                    icon={<Check className="h-4 w-4" />}
                    loading={updateStatusMutation.isPending && updateStatusMutation.variables === 'accepted'}
                  >
                    Mark as Accepted
                  </PremiumButton>
                  <PremiumButton
                    variant="destructive"
                    onClick={() => handleStatusChange('rejected')}
                    icon={<X className="h-4 w-4" />}
                    loading={updateStatusMutation.isPending && updateStatusMutation.variables === 'rejected'}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    Mark as Rejected
                  </PremiumButton>
                  <PremiumButton
                    variant="outline"
                    onClick={() => setIsEditModalOpen(true)}
                    icon={<FileEdit className="h-4 w-4" />}
                  >
                    Edit Estimate
                  </PremiumButton>
                </>
              )}
              
              {estimate.status === 'accepted' && (
                <PremiumButton
                  variant="default"
                  onClick={handleConvertToInvoice}
                  icon={<BanknoteIcon className="h-4 w-4" />}
                  loading={isConvertingToInvoice}
                >
                  Create Work Order
                </PremiumButton>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="premium-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Financial Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>{formatCurrency(estimate.subtotal)}</span>
              </div>
              {parseFloat(estimate.tax) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxes:</span>
                  <span>{formatCurrency(estimate.tax)}</span>
                </div>
              )}
              {parseFloat(estimate.discount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Discount:</span>
                  <span>-{formatCurrency(estimate.discount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-bold">
                <span>Total:</span>
                <span className="text-lg bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent">
                  {formatCurrency(estimate.total)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Client and Project Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <InfoCard
          title="Client Information"
          icon={<User className="h-4 w-4" />}
          content={
            <div className="mt-2">
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {estimate.client?.firstName?.[0]}{estimate.client?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{estimate.client?.firstName} {estimate.client?.lastName}</p>
                  <p className="text-sm text-muted-foreground">{estimate.client?.company || "Individual Client"}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 text-sm">
                <div className="flex items-center">
                  <span className="text-muted-foreground w-20">Email:</span>
                  <span>{estimate.client?.email}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-muted-foreground w-20">Phone:</span>
                  <span>{formatPhone(estimate.client?.phone)}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-muted-foreground w-20">Address:</span>
                  <span>{estimate.client?.address}</span>
                </div>
              </div>
            </div>
          }
        />
        
        {estimate.project && (
          <InfoCard
            title="Project Information"
            icon={<Briefcase className="h-4 w-4" />}
            content={
              <div className="mt-2">
                <h3 className="font-medium">{estimate.project?.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-3">
                  {estimate.project?.description}
                </p>
                <div className="mt-2 text-sm">
                  <div className="flex items-center">
                    <CalendarRange className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                    <span className="text-muted-foreground">Periodo: </span>
                    <span className="ml-1">
                      {estimate.project?.startDate && format(new Date(estimate.project.startDate), 'dd MMM yyyy', { locale: es })}
                      {estimate.project?.endDate && ` - ${format(new Date(estimate.project.endDate), 'dd MMM yyyy', { locale: es })}`}
                    </span>
                  </div>
                </div>
              </div>
            }
          />
        )}
        
        <InfoCard
          title="Important Dates"
          icon={<CalendarRange className="h-4 w-4" />}
          content={
            <div className="mt-2 space-y-2">
              <DateDisplay 
                label="Issue Date" 
                date={estimate.issueDate}
              />
              
              {estimate.expiryDate && (
                <DateDisplay 
                  label="Expiration" 
                  date={estimate.expiryDate}
                  isPast={isExpired}
                  isFuture={!isExpired}
                  daysRemaining={daysUntilExpiry || undefined}
                />
              )}
            </div>
          }
        />
      </div>
      
      {/* Estimate Items */}
      <div className="mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileSpreadsheet className="h-5 w-5 mr-2" />
              Estimate Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ItemTable
              items={estimate.items || []}
              subtotal={estimate.subtotal}
              tax={estimate.tax}
              discount={estimate.discount}
              total={estimate.total}
                              emptyMessage="This estimate has no items"
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Terms, Notes and Signatures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="md:h-full flex flex-col">
          <CardHeader>
            <CardTitle>Terms and Conditions</CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <div className="rounded-md bg-muted/50 p-4 h-full">
              <p className="whitespace-pre-line">
                {estimate.terms || "No terms and conditions have been specified."}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-1 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="whitespace-pre-line">
                  {getDisplayNotes(estimate.notes) || "No additional notes for this estimate."}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Signatures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <SignatureDisplay
                  title="Contractor Signature"
                  signature={estimate.contractorSignature}
                  date={estimate.issueDate ? new Date(estimate.issueDate) : undefined}
                />
                
                <SignatureDisplay
                  title="Client Signature"
                  signature={estimate.clientSignature}
                  date={estimate.status === 'accepted' ? estimate.updatedAt ? new Date(estimate.updatedAt) : undefined : undefined}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-[min(1200px,95vw)] w-full max-h-[min(900px,95vh)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit estimate</DialogTitle>
          </DialogHeader>
          {estimate && (
            <EstimateForm
              estimateId={estimate.id}
              clientId={estimate.clientId ?? estimate.client_id}
              projectId={estimate.projectId ?? estimate.project?.id ?? undefined}
              onCancel={() => setIsEditModalOpen(false)}
              onSuccess={() => {
                void refetch();
                setIsEditModalOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}